import { useEffect, useState } from 'react';
import {
  createFilterPreset,
  deleteFilterPreset,
  updateFilterPreset,
} from '../api/filterPresets';

export default function FilterPresetsModal({
  presets = [],
  currentQuery,
  onClose,
  onChanged,
}) {
  const [items, setItems] = useState(presets);
  const [newName, setNewName] = useState('');
  const [draftNames, setDraftNames] = useState({});
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setItems(presets);
    setDraftNames(Object.fromEntries(presets.map((p) => [p.id, p.name])));
  }, [presets]);

  async function refreshAfter(action) {
    const next = await action();
    if (onChanged) {
      await onChanged();
    }
    return next;
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError('');
    setCreating(true);
    try {
      await refreshAfter(() =>
        createFilterPreset({ name: newName, query: currentQuery }),
      );
      setNewName('');
    } catch (err) {
      setError(err.message || 'Ошибка создания');
    } finally {
      setCreating(false);
    }
  }

  async function handleRename(preset) {
    const name = (draftNames[preset.id] ?? preset.name).trim();
    if (!name || name === preset.name) {
      return;
    }
    setError('');
    setBusyId(preset.id);
    try {
      await refreshAfter(() => updateFilterPreset(preset.id, { name }));
    } catch (err) {
      setError(err.message || 'Ошибка переименования');
    } finally {
      setBusyId(null);
    }
  }

  async function handleOverwrite(preset) {
    setError('');
    setBusyId(preset.id);
    try {
      await refreshAfter(() => updateFilterPreset(preset.id, { query: currentQuery }));
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(preset) {
    if (!window.confirm(`Удалить пресет «${preset.name}»?`)) {
      return;
    }
    setError('');
    setBusyId(preset.id);
    try {
      await refreshAfter(() => deleteFilterPreset(preset.id));
    } catch (err) {
      setError(err.message || 'Ошибка удаления');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm dark:bg-slate-950/80"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-presets-modal-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0">
            <h2
              id="filter-presets-modal-title"
              className="text-lg font-semibold text-slate-900 dark:text-white"
            >
              Пресеты фильтров
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Общие для всех. Сохраняется полный набор фильтров из адресной строки.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-sm text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-300"
          >
            Закрыть
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error ? (
            <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </p>
          ) : null}

          <form onSubmit={handleCreate} className="mb-4 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Имя нового пресета"
              maxLength={80}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
            <button
              type="submit"
              disabled={creating || !newName.trim() || !currentQuery}
              className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
            >
              {creating ? 'Создаём…' : 'Создать из текущих'}
            </button>
          </form>

          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">Пока нет пресетов</p>
          ) : (
            <ul className="space-y-2">
              {items.map((preset) => {
                const busy = busyId === preset.id;
                return (
                  <li
                    key={preset.id}
                    className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-800"
                  >
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={draftNames[preset.id] ?? preset.name}
                        onChange={(event) =>
                          setDraftNames((prev) => ({ ...prev, [preset.id]: event.target.value }))
                        }
                        onBlur={() => handleRename(preset)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                        }}
                        maxLength={80}
                        disabled={busy}
                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-medium text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          disabled={busy || !currentQuery}
                          onClick={() => handleOverwrite(preset)}
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-slate-400 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
                        >
                          Сохранить текущие
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleDelete(preset)}
                          className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50 dark:border-red-900 dark:text-red-300"
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
